import { StarIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import classNames from "src/helpers/classNames";
import Image from "next/image";
import { ValueWithImage } from "src/config/reviewSystemData";
import ThemeButton from "src/components/button/ThemeButton";
import { Dispatch, SetStateAction } from "react";
import { useTranslations } from "next-intl";
import BlackSpinIcon from "src/components/icon/BlackSpinIcon";
import { ReviewFormErrors } from "../ReviewFormModal";

interface ReviewPreviewProps {
  productToReview: string;
  rating: number;
  fit: ValueWithImage;
  daysWorn: { value: string; image: string }[];
  reviewTitle: string;
  reviewBody: string;
  height: string;
  weight: string;
  pantSize: string;
  sizePurchased: string;
  periodManagementOptions: ValueWithImage[];
  timeAsShemsiUser: string;
  reviewerName: string;
  reviewerAge: string;
  reviewerEmail: string;
  setReviewStep: Dispatch<SetStateAction<number>>;
  loadingSubmission: boolean;
  reviewFormErrors: ReviewFormErrors;
  reviewFormStatus: {
    message: string;
    success: boolean;
  };
}

const ReviewPreview = ({
  productToReview,
  rating,
  fit,
  daysWorn,
  reviewTitle,
  reviewBody,
  height,
  weight,
  pantSize,
  sizePurchased,
  periodManagementOptions,
  timeAsShemsiUser,
  reviewerName,
  reviewerAge,
  reviewerEmail,
  setReviewStep,
  loadingSubmission,
  reviewFormErrors,
  reviewFormStatus,
}: ReviewPreviewProps) => {
  const t = useTranslations("ReviewForm");
  return (
    <section className="flex h-auto flex-col items-center gap-8 pb-8">
      <h2 className="text-2xl font-semibold">
        {t("preview_your_review_for", { productToReview: productToReview })}
      </h2>

      {/* Rating */}
      <section className="w-full space-y-4">
        <div className="flex justify-between">
          <div className="flex items-center">
            {Array.from(Array(5).keys()).map((item, index) => (
              <StarIcon
                key={index}
                className={classNames(
                  `h-8 w-8 flex-shrink-0  text-black`,
                  index < rating ? "fill-amber-400" : ""
                )}
                aria-hidden="true"
              />
            ))}
          </div>
          <button
            onClick={() => setReviewStep(1)}
            className="flex items-center gap-1 text-sm font-semibold underline"
          >
            {t("edit")}
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {reviewFormErrors.rating && (
          <p className="error-message">{reviewFormErrors.rating}</p>
        )}
      </section>
      {/* Fit */}
      <section className="w-full space-y-4">
        <div className="flex justify-between">
          <p className="font-semibold">{t("fit")}</p>
          <button
            onClick={() => setReviewStep(2)}
            className="flex items-center gap-1 text-sm font-semibold underline"
          >
            {t("edit")}
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {fit.value && (
          <div className="flex items-center gap-2">
            <Image
              src={fit.image.src}
              alt="fit-icon"
              style={{
                width: `${fit.image.width - 8}px`,
                height: `${fit.image.height - 14}px`,
              }}
              sizes="4rem"
              width={0}
              height={0}
            />
            <p className="text-gray-600">{t(fit.value)}</p>
          </div>
        )}

        {reviewFormErrors.fit && (
          <p className="error-message">{reviewFormErrors.fit}</p>
        )}
      </section>

      {/* Days worn on */}
      <section className="w-full space-y-4">
        <div className="flex justify-between">
          <p className="font-semibold">{t("days_worn_on")}</p>
          <button
            onClick={() => setReviewStep(3)}
            className="flex items-center gap-1 text-sm font-semibold underline"
          >
            {t("edit")}
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {daysWorn.length > 0 && (
          <div className="flex gap-10">
            {daysWorn.map((day) => (
              <div key={day.value} className="flex gap-2">
                <Image
                  src={day.image}
                  alt="days-worn-icon"
                  className="h-6 w-4"
                  sizes="2rem"
                  width={0}
                  height={0}
                />
                <p className="text-gray-600">{t(day.value)}</p>
              </div>
            ))}
          </div>
        )}
        {reviewFormErrors.daysWorn && (
          <p className="error-message">{reviewFormErrors.daysWorn}</p>
        )}
      </section>

      {/* Review */}
      <section className="w-full space-y-4">
        <div className="flex justify-between">
          <p className="font-semibold">{t("your_review")}</p>
          <button
            onClick={() => setReviewStep(4)}
            className="flex items-center gap-1 text-sm font-semibold underline"
          >
            {t("edit")}
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {reviewTitle && reviewBody && (
          <>
            <p className="text-gray-600 text-start">{reviewTitle}</p>
            <p className="text-gray-600 text-start">{reviewBody}</p>
          </>
        )}
        {reviewFormErrors.review && (
          <p className="error-message">{reviewFormErrors.review}</p>
        )}
      </section>

      {/* Reviewer measurements */}
      <section className="w-full space-y-4">
        <div className="flex justify-between">
          <p className="font-semibold">{t("about_you")}</p>
          <button
            onClick={() => setReviewStep(5)}
            className="flex items-center gap-1 text-sm font-semibold underline"
          >
            {t("edit")}
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {(height || weight) && (
          <div className="flex gap-16">
            {height && (
              <p className="text-gray-600">
                {t("height_value", { height: height })}
              </p>
            )}
            {weight && (
              <p className="text-gray-600">
                {t("weight_value", { weight: weight })}
              </p>
            )}
          </div>
        )}
        <div className="flex gap-16">
          {pantSize && <p>{t("pants_size_value", { pantSize: pantSize })}</p>}
          <p className="text-gray-600">
            {t("size_purchased_value", {
              sizePurchased: sizePurchased.toUpperCase(),
            })}
          </p>
        </div>
        {reviewFormErrors.sizePurchased && (
          <p className="error-message">{reviewFormErrors.sizePurchased}</p>
        )}
      </section>

      {/* Period management */}
      <section className="w-full space-y-4">
        <div className="flex justify-between">
          <p className="font-semibold">{t("period_management")}</p>
          <button
            onClick={() => setReviewStep(6)}
            className="flex items-center gap-1 text-sm font-semibold underline"
          >
            {t("edit")}
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {periodManagementOptions.map((option) => (
            <div
              key={option.value}
              className="cols-span-1 flex items-center gap-4"
            >
              <Image
                src={option.image.src}
                alt="period-management-option-icon"
                style={{
                  width: `${option.image.width}px`,
                  height: `${option.image.height}px`,
                }}
                sizes="12rem"
                width={0}
                height={0}
              />
              <p className="text-gray-600">{t(option.value)}</p>
            </div>
          ))}
        </div>
        {reviewFormErrors.periodManagementOptions && (
          <p className="error-message">
            {reviewFormErrors.periodManagementOptions}
          </p>
        )}
      </section>

      {/* Shemsi user for period of time */}
      <section className="w-full space-y-4">
        <div className="flex justify-between">
          <p className="font-semibold">{t("shemsi_user")}</p>
          <button
            onClick={() => setReviewStep(7)}
            className="flex items-center gap-1 text-sm font-semibold underline"
          >
            {t("edit")}
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <p className="text-gray-600 text-start">{t(timeAsShemsiUser)}</p>
        {reviewFormErrors.timeAsShemsiUser && (
          <p className="error-message">{reviewFormErrors.timeAsShemsiUser}</p>
        )}
      </section>

      {/* Reviewer info */}
      <section className="w-full space-y-4">
        <div className="flex justify-between">
          <p className="font-semibold">{t("your_info")}</p>
          <button
            onClick={() => setReviewStep(8)}
            className="flex items-center gap-1 text-sm font-semibold underline"
          >
            {t("edit")}
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 text-start">
          <div className="col-span-1 flex flex-col gap-1">
            <p className="">{t("name")}</p>
            <p>{reviewerName}</p>
          </div>
          {reviewerAge && (
            <div className="col-span-1 flex flex-col gap-1">
              <p>{t("age")}</p>
              <p>{reviewerAge}</p>
            </div>
          )}
          <div className="col-span-1 flex flex-col gap-1">
            <p>{t("email")}</p>
            <p>{reviewerEmail}</p>
          </div>
        </div>
        {reviewFormErrors.reviewerFullname && (
          <p className="error-message">{reviewFormErrors.reviewerFullname}</p>
        )}
        {reviewFormErrors.reviewerEmail && (
          <p className="error-message">{reviewFormErrors.reviewerEmail}</p>
        )}
      </section>
      <section className="flex w-full flex-col items-center gap-4 border-t border-gray-600">
        <p className="pt-4">{t("your_review_is_anonymous")}</p>
        <ThemeButton type="submit" disabled={loadingSubmission}>
          {loadingSubmission && <BlackSpinIcon className="-ms-1 me-3" />}
          <span>{t("submit_review")}</span>
        </ThemeButton>
        {reviewFormStatus.message && (
          <p
            className={
              reviewFormStatus.success ? "!success-message" : "!error-message"
            }
          >
            {reviewFormStatus.message}
          </p>
        )}
      </section>
    </section>
  );
};

export default ReviewPreview;
